// Lo que el celular del vendedor guarda para trabajar sin señal (2026-10-25): el paquete del día
// (clientes, catálogo con precios y stock), la cola de pedidos/visitas por enviar, lo ya enviado y el
// pedido a medio digitar de cada cliente. Va en IndexedDB (el catálogo no cabe cómodo en localStorage);
// si el navegador no lo deja usar, cae a memoria: la app funciona, pero sin sobrevivir a un cierre.

export interface VendedorRef {
  id: string;
  codigo: string;
  nombre: string;
}

export interface ContextoVendedor {
  vendedor: VendedorRef;
  vendedores: VendedorRef[] | null;
  bodega: { id: string; codigo: string; nombre: string } | null;
  hoy: string;
}

export interface DireccionCliente {
  id: string;
  texto: string;
  comuna: string | null;
  despacho: boolean;
  principal: boolean;
}

export interface ClienteRuta {
  entidadId: string;
  rut: string | null;
  dv: string | null;
  razonSocial: string;
  nombreFantasia: string | null;
  diasVisita: string | null;
  ordenVisita: number[] | null;
  listaPrecioId: string | null;
  condicionPago: string;
  plazoDias: number;
  lineaCredito: string | null;
  bloqueado: boolean;
  motivoBloqueo: string | null;
  documentoPorDefecto: string | null;
  direcciones: DireccionCliente[];
  telefono: string | null;
  deuda: { deuda: string; vencidos: string; chequesEnCartera: string } | null;
  ultimaVisita: { fecha: string; resultado: string } | null;
  habituales: Array<{ productoId: string; veces: number; ultima: string }>;
  /** Código del vendedor del cliente (se muestra al buscar en todos los clientes). */
  vendedor?: string | null;
  vendedorId?: string | null;
  /** Condiciones de pago que la nota acepta para el cliente (paquetes viejos no las traen). */
  opcionesPago?: CondicionYPlazo[];
}

export interface CondicionYPlazo {
  condicion: string;
  plazoDias: number;
}

export interface PresentacionCat {
  id: string;
  codigo: string;
  nombre: string;
  factor: string;
  esBase: boolean;
}

export interface ProductoCat {
  id: string;
  sku: string;
  nombre: string;
  presentaciones: PresentacionCat[];
}

export interface CatalogoVendedor {
  generadoEn: string;
  bodega: { id: string; codigo: string; nombre: string } | null;
  listas: Array<{ id: string; codigo: string; nombre: string; semantica: string }>;
  /** Listas que el canal VENDEDOR permite además de la del cliente. */
  listasAlternativas?: string[];
  productos: ProductoCat[];
  /** listaId → presentacionId → valor. */
  precios: Record<string, Record<string, string>>;
  /** listaId → presentacionId → [[desde, valor], …]. */
  escalas: Record<string, Record<string, Array<[string, string]>>>;
  /** productoId → disponible en la bodega (unidades base). */
  stock: Record<string, string>;
}

/** Todo lo que el vendedor necesita para un día sin señal. */
export interface Paquete {
  empresaId: string;
  empresaNombre: string;
  usuarioNombre: string;
  /** Vendedor elegido por un supervisor («ver como»); null = el propio. */
  verComo: string | null;
  contexto: ContextoVendedor;
  clientes: ClienteRuta[];
  catalogo: CatalogoVendedor;
  guardadoEn: string;
}

export interface LineaBorrador {
  productoId: string;
  presentacionId: string;
  cantidad: string;
  /** Precio con IVA por unidad de la presentación, puesto a mano (permiso ventas.precio.manual). */
  precioManual?: string;
}

export interface Borrador {
  lineas: LineaBorrador[];
  /** Ya no se elige en el celular (Rodrigo 27-10): manda la ficha del cliente. */
  documento: 'FACTURA' | 'BOLETA' | null;
  /** Lo comercial que cambia quien tiene el permiso; null/ausente = lo del cliente. */
  listaPrecioId?: string | null;
  condicion?: CondicionYPlazo | null;
  vendedorNotaId?: string | null;
  autorizar?: boolean;
  entrega: 'DESPACHO' | 'RETIRO';
  direccionId: string | null;
  fechaEntrega: string | null;
  observaciones: string;
}

export interface ResultadoPedido {
  notaId: string | null;
  numero: string | null;
  estado: string;
  total: string | null;
  ajustes: Array<{
    productoId: string;
    sku: string;
    descripcion: string;
    pedido: string;
    entro: string;
  }>;
  aviso?: string | null;
}

export interface ItemCola {
  /** El `idExterno`: el mismo en cada reenvío. */
  id: string;
  tipo: 'PEDIDO' | 'VISITA';
  empresaId: string;
  /** Idempotency-Key fija del envío. */
  clave: string;
  cuerpo: Record<string, unknown>;
  cliente: string;
  /** Total aproximado calculado en el celular (el real lo da el ERP). */
  totalAprox: number | null;
  lineas: number;
  creadoEn: string;
  estado: 'PENDIENTE' | 'ERROR';
  intentos: number;
  error: string | null;
}

export interface ItemEnviado extends Omit<ItemCola, 'estado' | 'error' | 'intentos'> {
  enviadoEn: string;
  resultado: ResultadoPedido | null;
}

const BASE = 'madeci-vendedor';
const TIENDA = 'kv';
const memoria = new Map<string, unknown>();

function abrir(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const r = indexedDB.open(BASE, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(TIENDA);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

let conexion: Promise<IDBDatabase | null> | null = null;
const db = () => (conexion ??= abrir());

export async function leer<T>(clave: string): Promise<T | null> {
  const d = await db();
  if (!d) return (memoria.get(clave) as T | undefined) ?? null;
  return new Promise((resolve) => {
    try {
      const r = d.transaction(TIENDA, 'readonly').objectStore(TIENDA).get(clave);
      r.onsuccess = () => resolve((r.result as T | undefined) ?? null);
      r.onerror = () => resolve((memoria.get(clave) as T | undefined) ?? null);
    } catch {
      resolve((memoria.get(clave) as T | undefined) ?? null);
    }
  });
}

export async function escribir<T>(clave: string, valor: T | null): Promise<void> {
  if (valor === null) memoria.delete(clave);
  else memoria.set(clave, valor);
  const d = await db();
  if (!d) return;
  await new Promise<void>((resolve) => {
    try {
      const t = d.transaction(TIENDA, 'readwrite');
      const s = t.objectStore(TIENDA);
      if (valor === null) s.delete(clave);
      else s.put(valor, clave);
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

export const CLAVES = {
  paquete: 'paquete',
  cola: 'cola',
  enviados: 'enviados',
  borrador: (empresaId: string, clienteId: string) => `borrador:${empresaId}:${clienteId}`,
};

/** Identificador del celular para cada pedido o visita (va como `idExterno`). */
export function nuevoIdExterno(): string {
  const c = globalThis.crypto;
  const u = c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `cel-${u}`;
}

/** Solo para pruebas: olvida lo guardado en memoria (en el navegador real va en IndexedDB). */
export function olvidarEnMemoria(): void {
  memoria.clear();
}
